/**
 * Area Calculator
 * Calculates total area (bottom + walls) for various shapes
 */

const AreaCalculator = {
  /**
   * Initialize calculator for a specific instance
   * @param {string} uniqueId - Unique identifier for this calculator instance
   * @param {Array} calculators - Array of calculator configurations
   */
  init(uniqueId, calculators = []) {
    const container = document.getElementById(`area-calculator-${uniqueId}`);
    if (!container) return;

    calculators.forEach((calc, index) => {
      if (!calc.enabled) return;

      const calcId = `calc-${index}`;
      const calcContainer = container.querySelector(`[data-calculator-id="${calcId}"]`);
      if (!calcContainer) return;

      // Get all input groups for this calculator
      const inputGroups = calcContainer.querySelectorAll('.area-calculator__input-group');
      const resultElement = calcContainer.querySelector(`[data-result-id="${calcId}"]`);

      if (!resultElement) return;

      inputGroups.forEach((group) => {
        const input = group.querySelector('input[data-dimension]');
        const unit = group.querySelector('.area-calculator__unit');
        if (!input) return;

        // Wrap input in step button structure (like product-area-calculator)
        const inputContainer = document.createElement('div');
        inputContainer.className = 'area-calculator__input-container';

        const wrapper = document.createElement('div');
        wrapper.className = 'area-calculator__input-wrapper';

        const step = parseFloat(input.step) || 0.01;
        const min = parseFloat(input.min) || 0.01;
        const precision = step < 0.01 ? 1000 : 100;

        const minusBtn = document.createElement('button');
        minusBtn.type = 'button';
        minusBtn.className = 'area-calculator__step-btn';
        minusBtn.setAttribute('aria-label', 'Decrease');
        minusBtn.textContent = '−';
        minusBtn.addEventListener('click', () => {
          const val = parseFloat(input.value) || min;
          const next = Math.max(min, Math.round((val - step) * precision) / precision);
          input.value = next;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });

        const plusBtn = document.createElement('button');
        plusBtn.type = 'button';
        plusBtn.className = 'area-calculator__step-btn';
        plusBtn.setAttribute('aria-label', 'Increase');
        plusBtn.textContent = '+';
        plusBtn.addEventListener('click', () => {
          const val = parseFloat(input.value) || min;
          const next = Math.round((val + step) * precision) / precision;
          input.value = next;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });

        if (unit) unit.remove();
        wrapper.appendChild(input);
        if (unit) wrapper.appendChild(unit);

        inputContainer.appendChild(minusBtn);
        inputContainer.appendChild(wrapper);
        inputContainer.appendChild(plusBtn);

        // Replace input (and unit) with new structure - insert after label
        const label = group.querySelector('.area-calculator__label');
        if (label && label.nextSibling) {
          while (label.nextSibling) label.nextSibling.remove();
        }
        group.appendChild(inputContainer);
      });

      // Get inputs again after restructuring
      const inputs = calcContainer.querySelectorAll('input[data-dimension]');

      inputs.forEach((input) => {
        input.addEventListener('input', () => {
          this.calculate(uniqueId, calcId, calc.type, calcContainer);
        });
      });

      // Calculate initial result if all required inputs have values
      const allInputsHaveValues = Array.from(inputs).every(
        (input) => input.value && parseFloat(input.value) > 0
      );
      if (allInputsHaveValues && inputs.length > 0) {
        this.calculate(uniqueId, calcId, calc.type, calcContainer);
      }
    });
  },

  /**
   * Calculate area based on calculator type
   * @param {string} uniqueId - Unique identifier for this calculator instance
   * @param {string} calcId - Calculator identifier
   * @param {string} type - Calculator type: 'cylinder', 'rectangle', 'rectangular-box'
   * @param {HTMLElement} calcContainer - Calculator container element
   */
  calculate(uniqueId, calcId, type, calcContainer) {
    const resultElement = calcContainer.querySelector(`[data-result-id="${calcId}"]`);
    if (!resultElement) return;

    let totalArea = null;

    switch (type) {
      case 'cylinder': {
        const diameterInput = calcContainer.querySelector('[data-dimension="diameter"]');
        const heightInput = calcContainer.querySelector('[data-dimension="height"]');

        if (!diameterInput || !heightInput) return;

        const diameter = parseFloat(diameterInput.value);
        const height = parseFloat(heightInput.value);

        if (isNaN(diameter) || isNaN(height) || diameter <= 0 || height <= 0) {
          this.updateResult(resultElement, null);
          return;
        }

        const radius = diameter / 2;
        const PI = Math.PI;

        // Bottom area = π * r²
        const bottomArea = PI * radius * radius;

        // Wall area = circumference * height = π * d * h
        const wallArea = PI * diameter * height;

        // Total area = bottom + walls
        totalArea = bottomArea + wallArea;
        break;
      }

      case 'rectangle': {
        const lengthInput = calcContainer.querySelector('[data-dimension="length"]');
        const widthInput = calcContainer.querySelector('[data-dimension="width"]');

        if (!lengthInput || !widthInput) return;

        const length = parseFloat(lengthInput.value);
        const width = parseFloat(widthInput.value);

        if (isNaN(length) || isNaN(width) || length <= 0 || width <= 0) {
          this.updateResult(resultElement, null);
          return;
        }

        // Only bottom area (no walls since no height)
        totalArea = length * width;
        break;
      }

      case 'rectangular-box': {
        const lengthInput = calcContainer.querySelector('[data-dimension="length"]');
        const widthInput = calcContainer.querySelector('[data-dimension="width"]');
        const heightInput = calcContainer.querySelector('[data-dimension="height"]');

        if (!lengthInput || !widthInput || !heightInput) return;

        const length = parseFloat(lengthInput.value);
        const width = parseFloat(widthInput.value);
        const height = parseFloat(heightInput.value);

        if (
          isNaN(length) ||
          isNaN(width) ||
          isNaN(height) ||
          length <= 0 ||
          width <= 0 ||
          height <= 0
        ) {
          this.updateResult(resultElement, null);
          return;
        }

        // Bottom area = length * width
        const bottomArea = length * width;

        // Wall area = 2 * (length * height) + 2 * (width * height)
        const wallArea = 2 * length * height + 2 * width * height;

        // Total area = bottom + walls
        totalArea = bottomArea + wallArea;
        break;
      }

      default:
        this.updateResult(resultElement, null);
        return;
    }

    this.updateResult(resultElement, totalArea);
  },

  /**
   * Update the result display
   * @param {HTMLElement} resultElement - The result container element
   * @param {number|null} area - The calculated area, or null if invalid
   */
  updateResult(resultElement, area) {
    if (!resultElement) return;

    const numberElement = resultElement.querySelector('.area-calculator__result-number');
    if (!numberElement) return;

    if (area === null || isNaN(area)) {
      numberElement.textContent = '-';
      return;
    }

    // Format to 1 decimal place
    numberElement.textContent = area.toFixed(1);
  },
};

